import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Inbox } from 'lucide-react';

import { DataTable } from '@/components/glitch-ui/data-table';
import { landingPageLeadsQueryOptions } from '@/lib/query-options';
import type { LandingPageLead } from '@/lib/types';

function summarizeFields(data: Record<string, string>): string {
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');
}

export function LeadsTable({ publicId, organizationId }: { publicId: string; organizationId: number }) {
  const { data: leads = [] } = useQuery(landingPageLeadsQueryOptions(publicId, organizationId));

  const columns = useMemo<ColumnDef<LandingPageLead, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Submitted',
        cell: ({ row }) => (
          <span className="text-gray-900 whitespace-nowrap">
            {new Date(row.original.created_at).toLocaleString()}
          </span>
        ),
      },
      {
        id: 'fields',
        header: 'Fields',
        cell: ({ row }) => <span className="text-gray-900">{summarizeFields(row.original.data)}</span>,
      },
      {
        id: 'gclid',
        header: 'gclid',
        cell: ({ row }) =>
          row.original.gclid ? (
            <span className="inline-flex px-2 py-1 text-xs rounded bg-[#D1FAE5] text-[#047857]">Captured</span>
          ) : (
            <span className="text-gray-500">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <DataTable
      data={leads}
      columns={columns}
      pageSize={10}
      emptyIcon={Inbox}
      emptyTitle="No Leads Yet"
      emptyDescription="Form submissions from the published page will appear here."
    />
  );
}
